import { useAxios, useFeatureFlags } from '@/app/composable';
import store from '@/app/store';
import { AccessLevel } from '@/modules/access-policy/constants/access-levels.constants';
import { ContractsAPI } from '@/modules/sharing/api';
import { computed, ref, Ref } from '@vue/composition-api';
import { AssetTypeId, StatusCode } from '../constants';
import { Asset } from '../types';

export function useAssetSharing(asset: Ref<Asset | undefined>, user: Ref<{ id: number; organisationId: number }>) {
    const { isEnabled } = useFeatureFlags();

    const { exec, loading } = useAxios(true);

    const activePendingContracts = ref<any[]>([]);

    const isBlockchainEnabled = computed(() => store.getters.sharing.isBlockchainEnabled);
    const ethNetwork = computed(() => store.state.sharing.network);
    const hasWallet = computed(() => store.getters.organisation.hasWallet);
    const isPublic = computed(() => asset.value && asset.value.accessLevel === AccessLevel.Public);
    const isSelectiveSharing = computed(() => asset.value && asset.value.accessLevel === AccessLevel.SelectiveSharing);
    const isDataset = computed(() => asset.value && asset.value.assetTypeId === AssetTypeId.Dataset);
    const isAvailable = computed(() => asset.value && asset.value.status === StatusCode.Available);
    const isLocal = computed<boolean>(() => !!asset.value?.metadata?.filenames?.length);

    const hasActivePendingContract = computed(
        () =>
            !!activePendingContracts.value.find(
                (item: any) =>
                    asset.value && (item.assetId === asset.value.id || item.barteringAssetId === asset.value.id),
            ),
    );

    const belongsToUserOrOrganisation = computed(() => {
        if (asset.value && user.value) {
            if (user.value.organisationId && asset.value.createdBy?.organisationId)
                return user.value.organisationId === asset.value.createdBy.organisationId;
            return user.value.id === asset.value.createdBy?.id;
        }
        return true;
    });

    const canBuyOrAcquire = computed(
        () =>
            isEnabled('sharing') &&
            !belongsToUserOrOrganisation.value &&
            isDataset.value &&
            isAvailable.value &&
            !isLocal.value &&
            !hasActivePendingContract.value,
    );
    const canBuy = computed(() => canBuyOrAcquire.value && isSelectiveSharing.value);
    const canAcquire = computed(() => canBuyOrAcquire.value && isPublic.value);
    const disabledBuy = computed(() => isBlockchainEnabled.value && (!ethNetwork.value || !hasWallet.value));
    const disabledAcquire = computed(() => loading.value);

    const disabledBuyTooltip = computed(() => {
        if (isBlockchainEnabled.value) {
            if (!ethNetwork.value)
                return 'Disconnected from Ethereum Network. You cannot buy/acquire any asset at this moment.';
            if (!hasWallet.value)
                return 'You are not allowed to buy an asset because your organisation does not have a wallet. Please contact the legal representative of your organisation to create/import one.';
        }
        return '';
    });

    const fetchActivePendingContracts = () => {
        exec(ContractsAPI.getActivePending()).then((res: any) => {
            activePendingContracts.value = res.data;
        });
    };

    const acquirePublicAsset = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const payload = {
                isBundle: false,
                contracts: [{ fields: [], duration: { type: 'forever', number: null }, assetId: asset.value?.id }],
            };
            exec(ContractsAPI.create(payload))
                .then(() => {
                    fetchActivePendingContracts();
                    resolve();
                })
                .catch((e) => reject(e));
        });
    };

    if (isEnabled('sharing')) fetchActivePendingContracts();

    return { canBuy, canAcquire, disabledBuy, disabledBuyTooltip, disabledAcquire, acquirePublicAsset };
}
